import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  REQUIRED_VEHICLE_IMAGE_KINDS,
  vehicleDocumentRequirements,
  vehiclePaperworkReady,
  type PartnerDocument,
  type PartnerDocumentKind,
  type Vehicle,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  ErrorState,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, LifeBuoy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BlackoutEditor } from '../components/BlackoutEditor.js';
import { CarBookings } from '../components/CarBookings.js';
import { CarDetailsForm } from '../components/CarDetailsForm.js';
import { CarReviews } from '../components/CarReviews.js';
import { CarSummary } from '../components/CarSummary.js';
import { DocumentRow } from '../components/DocumentRow.js';
import { DocumentUploadDialog } from '../components/DocumentUploadDialog.js';
import { HandoverItemsEditor } from '../components/HandoverItemsEditor.js';
import { PhotosPanel } from '../components/PhotosPanel.js';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { useSupport } from '../lib/support.js';

const LISTING_VARIANT = {
  PUBLISHED: 'success',
  IN_REVIEW: 'warning',
  REJECTED: 'danger',
  DRAFT: 'default',
  UNLISTED: 'outline',
} as const;

/**
 * Everything about one car, in the order it matters.
 *
 * The first three are the run a draft is taken through — what it is, what
 * it looks like, what covers it — and they are numbered and ticked off
 * because most visits to this page are a draft being finished. The last
 * three are its record: the days its owner has claimed, the hires it has
 * had, what guests said.
 *
 * Still tabs rather than a true wizard: a partner who came back only to
 * renew an insurance certificate should not have to walk past their own
 * daily rate to reach it.
 */
const STEPS = ['details', 'photos', 'paperwork', 'dates', 'bookings', 'reviews'] as const;
type Step = (typeof STEPS)[number];

function isStep(value: string | null): value is Step {
  return value !== null && (STEPS as readonly string[]).includes(value);
}

/** What a car still needs photographed before it can be sent for review. */
function missingPhotoKinds(car: Vehicle): number {
  const have = new Set(car.images.map((image) => image.kind));
  return REQUIRED_VEHICLE_IMAGE_KINDS.filter((kind) => !have.has(kind)).length;
}

/** Certificates not on file, or on file and no longer good. */
function missingPaperwork(documents: PartnerDocument[], vehicleId: string): number {
  return vehicleDocumentRequirements(documents, vehicleId).filter(
    (requirement) =>
      requirement.state === 'MISSING' ||
      requirement.state === 'EXPIRED' ||
      requirement.state === 'REJECTED',
  ).length;
}

/**
 * One tab, wearing its step number and whether it is settled.
 *
 * The count is the point. A partner looking at a draft wants to know what
 * is left without opening all four, and "2" beside Photos answers that
 * from the tab strip.
 */
function StepTab({
  step,
  index,
  outstanding,
  label,
}: {
  step: Step;
  index: number;
  /** null where the step is optional and can never be outstanding. */
  outstanding: number | null;
  label: string;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const done = outstanding === 0;

  return (
    <TabsTrigger value={step} className="gap-2">
      <span
        aria-hidden
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
          done
            ? 'bg-success/15 text-success'
            : outstanding
              ? 'bg-warning/20 text-warning'
              : 'bg-surface-sunken text-text-muted',
        )}
      >
        {done ? <Check className="size-3" /> : (outstanding ?? index + 1)}
      </span>
      {label}
      {done ? <span className="sr-only">{t('carDetail.stepDone')}</span> : null}
    </TabsTrigger>
  );
}

/**
 * Everything about one car, in one place.
 *
 * The fleet page used to be the whole of it: a card, and two dialogs hung
 * off it for photographs and dates. There was nowhere to correct a plate,
 * and a car's registration and insurance lived on the verification page —
 * a screen about the *partner* — where they were filed under the account
 * rather than under the car they belong to.
 */
export function CarDetailPage() {
  const { t } = useTranslation(['partner', 'common']);
  const { id = '' } = useParams();
  const { partner } = usePartner();
  const queryClient = useQueryClient();
  const { openSupport } = useSupport();

  const [uploading, setUploading] = useState<PartnerDocumentKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * The step lives in the URL. It costs nothing and it means "the insurance
   * on the Hilux" is a link somebody can be sent, and that a reload after
   * an upload does not drop you back on the first tab.
   */
  const [params, setParams] = useSearchParams();
  const fromParam = params.get('step');
  const step: Step = isStep(fromParam) ? fromParam : 'details';

  const goTo = (next: string) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('step', next);
    setParams(nextParams, { replace: true });
  };

  const {
    data: car,
    isPending,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api.getVehicle(id),
  });

  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.myDocuments(),
  });

  /** Submit, take down and put back up all land here. */
  const act = useMutation({
    mutationFn: (action: 'submit' | 'unlist' | 'relist') =>
      action === 'submit'
        ? api.submitListing(id)
        : action === 'unlist'
          ? api.unlistVehicle(id)
          : api.relistVehicle(id),
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicle', id] }),
        queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
      ]);
    },
  });

  const back = (
    <Link
      to="/fleet"
      className="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
    >
      <ChevronLeft className="size-4" />
      {t('nav.fleet')}
    </Link>
  );

  if (loadError) {
    return (
      <>
        {back}
        <ErrorState error={loadError} onRetry={() => void refetch()} />
      </>
    );
  }

  if (isPending || !car) {
    return (
      <>
        {back}
        <Skeleton className="mt-4 h-9 w-72" />
        <Skeleton className="mt-6 h-10 w-96 rounded-pill" />
        <Skeleton className="mt-4 h-96 rounded-card" />
      </>
    );
  }

  const photosLeft = missingPhotoKinds(car);
  const paperworkLeft = missingPaperwork(documents.data ?? [], car.id);
  // The API's gate, read the same way: review judges the whole car, so it
  // will not take one without its paperwork on file.
  const submittable =
    photosLeft === 0 &&
    vehiclePaperworkReady(vehicleDocumentRequirements(documents.data ?? [], car.id)) &&
    partner.status !== 'SUSPENDED';
  const draft = car.listingStatus === 'DRAFT' || car.listingStatus === 'REJECTED';

  const at = STEPS.indexOf(step);
  const previous = STEPS[at - 1];
  const next = STEPS[at + 1];

  /**
   * The end of the setup run, for a car that is not up yet.
   *
   * On the paperwork step rather than on the last tab: a wizard that walks
   * somebody through the three screens that matter and then makes them find
   * the button themselves has wasted the walk — and the three after it are
   * a record, not a step, so the run ends before them.
   */
  const finish =
    draft && step === 'paperwork' ? (
      <Button
        disabled={!submittable}
        isLoading={act.isPending}
        onClick={() => act.mutate('submit')}
      >
        {t('fleet.submit')}
      </Button>
    ) : null;

  const panel = (children: ReactNode) => (
    <div className="rounded-card border border-border-default bg-surface-raised p-5 md:p-6">
      {children}

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-border-default pt-4">
        {previous ? (
          <Button variant="ghost" onClick={() => goTo(previous)}>
            <ChevronLeft className="size-4" />
            {t(`carDetail.tab.${previous}`)}
          </Button>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          {next ? (
            <Button variant="outline" onClick={() => goTo(next)}>
              {t(`carDetail.tab.${next}`)}
              <ChevronRight className="size-4" />
            </Button>
          ) : null}
          {finish}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {back}

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight">
            {car.year} {car.make} {car.model}
          </h1>
          <p className="mt-1 font-mono text-sm text-text-muted">{car.plateNumber}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={LISTING_VARIANT[car.listingStatus]}>
            {t(`common:listingStatus.${car.listingStatus}`)}
          </Badge>

          {/*
            Beside the listing's own state, which is what an owner is
            usually looking at when they want to ask about it — a rejection
            they do not understand, a submission that has sat in review.
            The same dialog as everywhere else, with this car on it.
          */}
          <Button
            variant="ghost"
            onClick={() =>
              openSupport({
                vehicleId: car.id,
                vehicleName: `${car.year} ${car.make} ${car.model}`,
                // Named, like every other contextual door — see
                // `SupportRequestContext.subject`.
                subject: `${car.year} ${car.make} ${car.model}`,
              })
            }
          >
            <LifeBuoy className="size-4" />
            {t('common:support.entryLabel')}
          </Button>

          {/* The listing's own state, reachable from any tab. The setup
              run repeats the submit at the end of its last step; this is
              the copy somebody who only came back to re-submit will find. */}
          {draft ? (
            <Button
              disabled={!submittable}
              isLoading={act.isPending}
              onClick={() => act.mutate('submit')}
            >
              {t('fleet.submit')}
            </Button>
          ) : null}

          {car.listingStatus === 'PUBLISHED' ? (
            <Button variant="ghost" isLoading={act.isPending} onClick={() => act.mutate('unlist')}>
              {t('fleet.unlist')}
            </Button>
          ) : null}

          {car.listingStatus === 'UNLISTED' ? (
            <Button isLoading={act.isPending} onClick={() => act.mutate('relist')}>
              {t('fleet.relist')}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      ) : null}

      {/* A rejection is only useful if the partner can read it where they
          act on it, not buried in an email. */}
      {car.listingStatus === 'REJECTED' && car.reviewNote ? (
        <Alert variant="warning" className="mt-4">
          <span className="font-semibold">{t('fleet.reviewNote')}:</span> {car.reviewNote}
        </Alert>
      ) : null}

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_19rem] xl:grid-cols-[minmax(0,1fr)_21rem]">
        {/*
          On a phone the picture comes first — it is how you know you are on
          the right car — and the steps follow. On a desktop it moves to the
          right and stays put while the panels scroll under it.
        */}
        <aside className="order-first lg:order-last lg:sticky lg:top-8">
          <CarSummary car={car} />
        </aside>

        <Tabs value={step} onValueChange={goTo} className="min-w-0">
          <TabsList className="max-w-full flex-wrap">
            <StepTab step="details" index={0} outstanding={0} label={t('carDetail.tab.details')} />
            <StepTab
              step="photos"
              index={1}
              outstanding={photosLeft}
              label={t('carDetail.tab.photos')}
            />
            <StepTab
              step="paperwork"
              index={2}
              outstanding={paperworkLeft}
              label={t('carDetail.tab.paperwork')}
            />
            {/* The last three are a record rather than a step: nothing is
              owed on them, so they carry their number and no verdict. */}
            <StepTab step="dates" index={3} outstanding={null} label={t('carDetail.tab.dates')} />
            <StepTab
              step="bookings"
              index={4}
              outstanding={null}
              label={t('carDetail.tab.bookings')}
            />
            <StepTab
              step="reviews"
              index={5}
              outstanding={null}
              label={t('carDetail.tab.reviews')}
            />
          </TabsList>

          <TabsContent value="details">
            {panel(
              <>
                <h2 className="font-display text-lg font-bold">{t('carDetail.tab.details')}</h2>
                <p className="mb-5 mt-0.5 text-sm text-text-muted">{t('carDetail.detailsHint')}</p>
                {/*
                Keyed on the car, so a refetch that brings back different
                values rebuilds the fields rather than leaving the form
                showing what was typed against a row that has moved on.
              */}
                <CarDetailsForm key={car.id} vehicle={car} />

                {/*
                  Under the details rather than a tab of its own: it is a
                  fact about the car, like its seats, and it is not a step a
                  draft has to finish — a car with no list hands over fine.
                  Its own save, though, because the form above sends a live
                  car back for review and this list never does.
                */}
                <section className="mt-8 border-t border-border-default pt-6">
                  <h2 className="font-display text-lg font-bold">{t('carDetail.items.title')}</h2>
                  <p className="mb-5 mt-0.5 text-sm text-text-muted">{t('carDetail.items.hint')}</p>
                  <HandoverItemsEditor key={car.id} vehicle={car} />
                </section>
              </>,
            )}
          </TabsContent>

          <TabsContent value="photos">
            {panel(
              <>
                <h2 className="font-display text-lg font-bold">{t('carDetail.tab.photos')}</h2>
                <PhotosPanel vehicle={car} />
              </>,
            )}
          </TabsContent>

          <TabsContent value="paperwork">
            {panel(
              <>
                {/*
                This car's registration, insurance and roadworthy — moved
                off the verification page, which is about the partner. An
                owner renewing insurance thinks about the car it covers,
                not about their own account.
              */}
                <h2 className="font-display text-lg font-bold">{t('carDetail.paperwork')}</h2>
                <p className="mb-5 mt-0.5 text-sm text-text-muted">
                  {t('carDetail.paperworkHint')}
                </p>

                <ul className="flex flex-col gap-2">
                  {vehicleDocumentRequirements(documents.data ?? [], car.id).map((requirement) => (
                    <DocumentRow
                      key={requirement.kind}
                      requirement={requirement}
                      onUpload={() => setUploading(requirement.kind)}
                    />
                  ))}
                </ul>
                <p className="mt-3 text-xs text-text-muted">{t('carDetail.paperworkWhy')}</p>
              </>,
            )}
          </TabsContent>

          <TabsContent value="dates">
            {panel(
              <>
                <h2 className="font-display text-lg font-bold">{t('calendar.title')}</h2>
                <BlackoutEditor vehicle={car} />
              </>,
            )}
          </TabsContent>

          <TabsContent value="bookings">
            {panel(
              <>
                <h2 className="font-display text-lg font-bold">{t('carDetail.tab.bookings')}</h2>
                <p className="mb-5 mt-0.5 text-sm text-text-muted">{t('carDetail.bookingsHint')}</p>
                <CarBookings vehicleId={car.id} />
              </>,
            )}
          </TabsContent>

          <TabsContent value="reviews">
            {panel(
              <>
                <h2 className="font-display text-lg font-bold">{t('carDetail.tab.reviews')}</h2>
                <p className="mb-5 mt-0.5 text-sm text-text-muted">{t('carDetail.reviewsHint')}</p>
                <CarReviews vehicleId={car.id} />
              </>,
            )}
          </TabsContent>
        </Tabs>
      </div>

      {uploading ? (
        <DocumentUploadDialog
          kind={uploading}
          vehicleId={car.id}
          onClose={() => setUploading(null)}
        />
      ) : null}
    </>
  );
}
